import { getSentryEnvironment } from "Utilities/Sentry/getSentryEnvironment";

import { getSmartyKey } from "../../config";

/**
 * Raw HTTP boundary for Smarty's US Autocomplete Pro endpoint. Extracted
 * from `smartyAutocomplete.js` so this file can be swapped out wholesale
 * in tests + Storybook (see `testUtils/MockSmartyFetch.js`). The mapper
 * stays next door — this layer only does the network call and returns
 * the parsed JSON body.
 *
 * Endpoint: https://us-autocomplete-pro.api.smarty.com/lookup
 * Auth: browser-side "website key" with a hostname allowlist configured
 * in the Smarty dashboard.
 *
 * Activation mirrors `providerRegistry.js`: real calls require
 * REACT_APP_ADDRESS_VALIDATION_LIVE=true at build time. In dev with
 * LIVE=false we delegate to MockSmartyFetch (same fixtures tests +
 * Storybook use). Production builds with LIVE=false silently no-op.
 *
 * `IS_LIVE` is a module-top literal so Terser folds the !IS_LIVE branch
 * away at LIVE=true build time, dead-code-eliminating the dynamic import
 * of MockSmartyFetch from the production bundle.
 */
const IS_LIVE = process.env.REACT_APP_ADDRESS_VALIDATION_LIVE === "true";
const ENDPOINT = "https://us-autocomplete-pro.api.smarty.com/lookup";
// Keep ≥5: the multi-unit picker scores across the whole pool (max-entries unit
// type + bare-shell detection need the sibling rows), so 1 isn't enough even
// though only the top candidate is returned. See SMARTY.md "Ambiguous building".
const MAX_RESULTS = 5;

// Autocomplete fires once per debounced keystroke, so a sustained outage
// (especially a 402 quota exhaustion) would otherwise report the SAME failure
// to Sentry on every keystroke. Report each distinct failure kind — the HTTP
// status, or "network"/"parse" — at most once per page load.
const reportedFailures = new Set();

const reportSmartyFailure = (kind, error) => {
  if (reportedFailures.has(kind)) return;
  reportedFailures.add(kind);
  // Optional chaining: Sentry may not be initialized in every host (tests,
  // non-prod). Tag with the failure kind so an outage is greppable.
  window.Sentry?.captureException?.(error, {
    extra: { smartyAutocompleteFailure: kind },
  });
};

// Test-only: clear the once-per-session dedup so each test starts fresh.
export const __resetSmartyFailureReporting = () => reportedFailures.clear();

/**
 * Calls Smarty and returns the parsed JSON body (e.g.
 * `{suggestions: [{street_line, secondary, city, state, zipcode, entries}]}`).
 *
 * Returns `null` for the BENIGN non-results — no key, feature not live, or an
 * aborted in-flight request (normal react-query cancellation when a newer
 * keystroke supersedes this one). These are not failures and are not reported.
 *
 * THROWS on a real failure — a non-OK HTTP status (4xx/5xx, incl. 402 quota),
 * a network error, or malformed JSON — after reporting it to Sentry (throttled
 * per kind, see above). The throw becomes react-query's `isError` upstream,
 * which lets `useAddressAutocomplete` keep the whole flow invisible during an
 * outage instead of collapsing the error into "no suggestions" and stamping a
 * spurious unverifiable verdict.
 *
 * @param {string} query - Already trimmed; the caller is responsible for
 *   short-query gating.
 * @param {{ country?: string, signal?: AbortSignal }} [options]
 *   `country` is informational — Smarty's US Autocomplete Pro endpoint
 *   only covers the US, so the caller (smartyAutocomplete.js) gates
 *   non-US away before we hit the wire. Surfacing it here gives the
 *   mock fetch override visibility into which country the consumer
 *   thinks it's searching, so tests can assert.
 * @returns {Promise<object | null>}
 */
export const fetchSmartyLookup = async (query, options = {}) => {
  if (!IS_LIVE) {
    // Fail closed: if a production deploy somehow has LIVE !== "true", never
    // load the test-only mock or serve its fixture data — no-op instead.
    if (getSentryEnvironment() === "production") return null;
    const { fetchSmartyLookup: mockFetch } = await import(
      "testUtils/MockSmartyFetch"
    );
    return mockFetch(query, options);
  }
  const key = getSmartyKey();
  if (!key) return null;
  const { signal } = options;
  if (signal && signal.aborted) return null;

  const url = new URL(ENDPOINT);
  url.searchParams.set("key", key);
  url.searchParams.set("search", query);
  url.searchParams.set("max_results", String(MAX_RESULTS));

  let response;
  try {
    response = await fetch(url.toString(), {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
  } catch (err) {
    // Abort is cancellation, not an outage: stay benign (null), don't report.
    if (err && err.name === "AbortError") return null;
    reportSmartyFailure("network", err);
    throw err;
  }
  if (!response.ok) {
    const error = new Error(`Smarty autocomplete HTTP ${response.status}`);
    error.status = response.status;
    reportSmartyFailure(String(response.status), error);
    throw error;
  }

  try {
    return await response.json();
  } catch (err) {
    // The signal can fire mid-body-read, rejecting json() with an AbortError;
    // that's cancellation, not malformed JSON, so stay benign and don't report.
    if (err && err.name === "AbortError") return null;
    reportSmartyFailure("parse", err);
    throw err;
  }
};
